import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Download, RotateCcw, Phone, CheckCircle2, TrendingUp, Zap } from 'lucide-react';
import { Progress } from '../ui/progress';

const ResultsDisplay = ({ formData, results, onStartOver, onDownloadPDF }) => {
  if (!results) return null;
  
  const getRecommendedUnits = () => {
    const units = [
      { size: 9000, label: '9K BTU', kw: 2.6 },
      { size: 12000, label: '12K BTU', kw: 3.5 },
      { size: 18000, label: '18K BTU', kw: 5.3 },
      { size: 24000, label: '24K BTU', kw: 7.0 },
      { size: 30000, label: '30K BTU', kw: 8.8 },
      { size: 36000, label: '36K BTU', kw: 10.5 }
    ];
    
    const totalWithMargin = results.totalBTU * 1.15; // 15% safety margin
    
    return units.filter(unit => 
      unit.size >= results.totalBTU && unit.size <= totalWithMargin
    ).slice(0, 3);
  };
  
  const recommendedUnits = getRecommendedUnits();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_hvac-calculator/artifacts/6t9eyga4_Algarve-Seasons%20logo.jpg" 
                alt="Algarve Seasons" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={onStartOver}
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              <Button 
                onClick={onDownloadPDF}
                className="bg-blue-700 hover:bg-blue-800 text-white shadow-md"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Success Message */}
        <Card className="mb-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
          <div className="flex items-center space-x-4">
            <div className="bg-green-500 rounded-full p-3">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Calculation Complete!</h2>
              <p className="text-gray-600 mt-1">Here are your personalized BTU requirements</p>
            </div>
          </div>
        </Card>
        
        {/* Primary Results */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-700 to-blue-800 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <Zap className="w-10 h-10 text-blue-200" />
              <span className="text-sm bg-blue-600 px-3 py-1 rounded-full">Total BTU</span>
            </div>
            <p className="text-5xl font-bold mb-2">{results.totalBTU.toLocaleString()}</p>
            <p className="text-blue-100">BTU/hour required</p>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-cyan-600 to-blue-600 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-10 h-10 text-cyan-200" />
              <span className="text-sm bg-cyan-700 px-3 py-1 rounded-full">kW</span>
            </div>
            <p className="text-5xl font-bold mb-2">{results.totalKW.toFixed(2)}</p>
            <p className="text-cyan-100">Kilowatts</p>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle2 className="w-10 h-10 text-indigo-200" />
              <span className="text-sm bg-indigo-700 px-3 py-1 rounded-full">Confidence</span>
            </div>
            <p className="text-5xl font-bold mb-2">{results.confidence}%</p>
            <p className="text-indigo-100">Accuracy level</p>
          </Card>
        </div>
        
        {/* Recommended Units */}
        <Card className="p-8 mb-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Recommended AC Units</h3>
          {recommendedUnits.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recommendedUnits.map((unit, index) => (
                <Card 
                  key={unit.size}
                  className={`p-6 border-2 transition-all duration-300 hover:shadow-lg ${
                    index === 0 
                      ? 'border-blue-700 bg-blue-50' 
                      : 'border-blue-200 hover:border-blue-400'
                  }`}
                >
                  {index === 0 && (
                    <div className="bg-blue-700 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">
                      RECOMMENDED
                    </div>
                  )}
                  <p className="text-3xl font-bold text-blue-900 mb-2">{unit.label}</p>
                  <p className="text-gray-600 mb-1">{unit.kw} kW</p>
                  <p className="text-sm text-gray-500">Perfect for your space</p>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-6 bg-amber-50 border-amber-200">
              <p className="text-amber-900 font-medium">Your space requires a larger unit. We recommend contacting us for a custom solution.</p>
            </Card>
          )}
        </Card>
        
        {/* Detailed Breakdown */}
        <Card className="p-8 mb-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Calculation Breakdown</h3>
          
          <div className="space-y-4">
            {Object.entries(results.breakdown).map(([key, value]) => {
              const percentage = (value / results.totalBTU * 100).toFixed(1);
              const labels = {
                base: 'Base Room Load',
                insulation: 'Insulation Factor',
                sun: 'Sun Exposure',
                occupants: 'Occupants',
                appliances: 'Appliances',
                other: 'Other Factors'
              };
              
              return (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{labels[key]}</span>
                    <span className="text-gray-900 font-bold">
                      {value.toLocaleString()} BTU ({percentage}%)
                    </span>
                  </div>
                  <Progress value={parseFloat(percentage)} className="h-3" />
                </div>
              );
            })}
          </div>
          
          <Card className="mt-6 p-4 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>Safety Margin:</strong> We recommend adding 10-20% to the calculated capacity 
              to account for extreme conditions and ensure optimal performance.
            </p>
          </Card>
        </Card>
        
        {/* Room Details Summary */}
        <Card className="p-8 mb-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Room Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Room Dimensions</p>
              <p className="text-lg font-bold text-gray-900">
                {formData.roomLength}m × {formData.roomWidth}m × {formData.roomHeight}m
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Room Area</p>
              <p className="text-lg font-bold text-gray-900">{results.roomArea.toFixed(1)} m²</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Room Volume</p>
              <p className="text-lg font-bold text-gray-900">{results.roomVolume.toFixed(1)} m³</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Insulation</p>
              <p className="text-lg font-bold text-gray-900 capitalize">{formData.insulationQuality}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Windows</p>
              <p className="text-lg font-bold text-gray-900">
                {formData.numberOfWindows} {formData.windowType}-glazed
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Occupants</p>
              <p className="text-lg font-bold text-gray-900">{formData.numberOfOccupants} people</p>
            </div>
          </div>
        </Card>
        
        {/* Recommendations */}
        <Card className="p-8 mb-8 bg-gradient-to-r from-green-50 to-blue-50 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Energy Efficiency Tips</h3>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Install your AC unit on a north-facing wall or shaded area to improve efficiency</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Use programmable thermostats to maintain optimal temperature when you're away</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Regular maintenance and filter cleaning can improve efficiency by up to 15%</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Consider inverter technology units for significant energy savings</span>
            </li>
          </ul>
        </Card>
        
        {/* Contact CTA */}
        <Card className="p-8 bg-gradient-to-r from-blue-700 to-blue-900 text-white shadow-2xl">
          <div className="text-center">
            <Phone className="w-16 h-16 mx-auto mb-4 text-blue-200" />
            <h3 className="text-3xl font-bold mb-3">Ready for Professional Installation?</h3>
            <p className="text-blue-100 mb-6 text-lg">
              Contact Algarve Seasons for expert installation and maintenance services in the Algarve region
            </p>
            <Button 
              size="lg"
              className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6 shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Contact for Professional Assessment
            </Button>
          </div>
        </Card>
      </main>
      
    </div>
  );
};

export default ResultsDisplay;